import { CheckCircle2, MapPin, Calendar, Clock, User, Stethoscope } from "lucide-react";
import { usePublicLang } from "@/contexts/PublicLangContext";
import { PUBLIC_BOOKING } from "@/constants/testIds";

const BANNER_IMAGE = "https://images.pexels.com/photos/16571735/pexels-photo-16571735.jpeg";

export default function StepConfirmation({ confirmation, clinic, onNewBooking }) {
  const { t, lang } = usePublicLang();
  const locale = lang === "es" ? "es-ES" : "en-US";
  const start = new Date(confirmation.start_time);

  return (
    <div className="text-center" data-testid={PUBLIC_BOOKING.confirmationScreen}>
      <div className="w-full h-36 sm:h-44 rounded-2xl overflow-hidden mb-6">
        <img src={BANNER_IMAGE} alt="" className="w-full h-full object-cover" />
      </div>

      <CheckCircle2 className="w-12 h-12 text-[#6E7A65] mx-auto mb-3" />
      <h2 className="font-serif text-2xl sm:text-3xl font-medium tracking-tight">{t("step5.title")}</h2>
      <p className="text-[#5C554D] mt-2">{t("step5.subtitle")}</p>

      <div className="bg-white border border-[rgba(140,125,112,0.2)] rounded-2xl p-5 sm:p-6 mt-6 text-left space-y-4">
        <div className="flex items-center gap-3">
          <Stethoscope className="w-4 h-4 text-[var(--brand-primary)] shrink-0" />
          <div><p className="text-xs text-[#5C554D]">{t("step5.service")}</p><p className="font-medium">{confirmation.service_name}</p></div>
        </div>
        <div className="flex items-center gap-3">
          <User className="w-4 h-4 text-[var(--brand-primary)] shrink-0" />
          <div><p className="text-xs text-[#5C554D]">{t("step5.professional")}</p><p className="font-medium">{confirmation.professional_name}</p></div>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-[var(--brand-primary)] shrink-0" />
          <div><p className="text-xs text-[#5C554D]">{t("step5.date")}</p><p className="font-medium capitalize">{start.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}</p></div>
        </div>
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-[var(--brand-primary)] shrink-0" />
          <div><p className="text-xs text-[#5C554D]">{t("step5.time")}</p><p className="font-medium">{start.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}</p></div>
        </div>
        {confirmation.clinic_address && (
          <div className="flex items-center gap-3">
            <MapPin className="w-4 h-4 text-[var(--brand-primary)] shrink-0" />
            <div><p className="text-xs text-[#5C554D]">{t("step5.address")}</p><p className="font-medium">{confirmation.clinic_address}</p></div>
          </div>
        )}
      </div>

      <p className="text-xs text-[#5C554D] mt-4">{t("step5.pendingNote")}</p>

      {confirmation.payment_link && (
        <div className="bg-[var(--brand-secondary)]/40 border border-[var(--brand-primary)]/20 rounded-2xl p-4 mt-5 text-left" data-testid={PUBLIC_BOOKING.paymentLinkBanner}>
          <p className="text-sm font-medium">
            {confirmation.payment_link.is_deposit ? t("step5.depositRequired") : t("step5.paymentRequired")}
          </p>
          <p className="text-xs text-[#5C554D] mt-1">
            {t("step5.paymentAmount")}: ${confirmation.payment_link.amount}
          </p>
          <a
            href={confirmation.payment_link.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center mt-3 w-full h-12 bg-[var(--brand-accent)] text-white rounded-md text-sm font-medium uppercase tracking-[0.16em] transition-all active:scale-[0.98]"
            data-testid={PUBLIC_BOOKING.payNowButton}
          >
            {t("step5.payNow")}
          </a>
        </div>
      )}

      {clinic?.financing_enabled && (
        <div className="bg-[var(--brand-champagne)]/25 border border-[var(--brand-primary)]/25 rounded-2xl p-4 mt-5 text-left" data-testid={PUBLIC_BOOKING.financingBanner}>
          <p className="text-sm font-medium text-[#292522]">{t("landing.financing.title")}</p>
          {(clinic.financing_note || clinic.financing_provider) && (
            <p className="text-xs text-[#5C554D] mt-1">
              {clinic.financing_note}
              {clinic.financing_note && clinic.financing_provider ? " · " : ""}
              {clinic.financing_provider}
            </p>
          )}
          {clinic.financing_url && (
            <a
              href={clinic.financing_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center mt-3 w-full h-12 bg-[var(--brand-accent)] text-white rounded-md text-sm font-medium uppercase tracking-[0.16em] transition-all active:scale-[0.98]"
              data-testid={PUBLIC_BOOKING.financingLink}
            >
              {t("landing.financing.cta")}
            </a>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onNewBooking}
        className="w-full h-14 bg-transparent border border-[var(--brand-primary)] text-[var(--brand-primary)] rounded-md text-sm font-medium uppercase tracking-[0.16em] mt-6 transition-all active:scale-[0.98]"
        data-testid={PUBLIC_BOOKING.newBookingButton}
      >
        {t("step5.newBooking")}
      </button>
    </div>
  );
}
